import { useState, useEffect } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import CalculationFormulaModal from './CalculationFormulaModal';
import MemberStatusListTable from './MemberStatusListTable';

import LoadingAnimation from '../../../Animation/NewLoading.json';
import { ReactComponent as PersonSilhouetteSvg } from '../../../image/PersonSilhouette.svg';


export default function MemberStatusBox(props) {
  const {
    allMembers,
    salesData,
    startDate,
    endDate,
    calcDateText,
    selectedPartnerGym,
    isOpenPartnerGymSales,
    isLoadingAllMember,
    isLoading,
    setIsLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [selectedTab, setSelectedTab] = useState('new');
  const [newMembers, setNewMembers] = useState([]);
  const [reRegisteredMembers, setReRegisteredMembers] = useState([]);
  const [leaveMembers, setLeaveMembers] = useState([]);
  const [aliveMembers, setAliveMembers] = useState([]);
  const [newPrice, setNewPrice] = useState(0);
  const [reRegisteredPrice, setReRegisteredPrice] = useState(0);
  const [isViewFormulaModal, setIsViewFormulaModal] = useState(false);

  // 신규 / 재등록 / 이탈 / 유효 회원 구분 //
  useEffect(() => {
    if (isLoadingAllMember) return;

    const newNewMembers = [];
    const newReRegisteredMembers = [];
    const newAliveMembers = [];
    const newLeaveMembers = [];

    for (let i = 0; i < allMembers.length; i++) {
      let isNew = false;
      let isReRegistered = false;
      let isAlive = false;
      let isLeave = false;

      // 산정기간 내 startDate가 있는 usage 탐색 → 첫 이용권(j===0)이면 신규, 아니면 재등록 //
      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (startDate.isSameOrBefore(allMembers[i].usages[j].startDate, 'day')
          && endDate.isSameOrAfter(allMembers[i].usages[j].startDate, 'day')) {
          allMembers[i].startDate = allMembers[i].usages[j].startDate;
          if (j === 0) isNew = true;
          else isReRegistered = true;
          break;
        }
      }

      // 유효 회원: 산정기간 내 1일이라도 유효했던 회원 (overlap 체크) //
      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (moment(allMembers[i].usages[j].startDate).isSameOrBefore(endDate, 'day')
          && moment(allMembers[i].usages[j].endDate).isSameOrAfter(startDate, 'day')) {
          isAlive = true;
          break;
        }
      }

      // 이탈 회원: 마지막 이용권의 endDate가 산정기간 내이고 오늘 이전인 회원 //
      const today = moment();

      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (j === allMembers[i].usages.length - 1
          && startDate.isSameOrBefore(allMembers[i].usages[j].endDate, 'day')
          && endDate.isSameOrAfter(allMembers[i].usages[j].endDate, 'day')
          && today.isAfter(allMembers[i].usages[j].endDate, 'day')) {
          allMembers[i].endDate = allMembers[i].usages[j].endDate;
          isLeave = true;
          break;
        }
      }

      if (isNew) newNewMembers.push(allMembers[i]);
      if (isReRegistered) newReRegisteredMembers.push(allMembers[i]);
      if (isAlive) newAliveMembers.push(allMembers[i]);
      if (isLeave) newLeaveMembers.push(allMembers[i]);
    }

    // 신규/재등록: 최근 등록일 순 정렬 //
    const sortByStartDateDesc = (a, b) => {
      if (a.startDate < b.startDate) return 1;
      if (a.startDate > b.startDate) return -1;
      return 0;
    };

    newNewMembers.sort(sortByStartDateDesc);
    newReRegisteredMembers.sort(sortByStartDateDesc);

    // 이탈 회원: 최근 이탈일 순 정렬 //
    newLeaveMembers.sort((a, b) => {
      if (a.endDate < b.endDate) return 1;
      if (a.endDate > b.endDate) return -1;
      return 0;
    });

    setNewMembers(newNewMembers);
    setReRegisteredMembers(newReRegisteredMembers);
    setAliveMembers(newAliveMembers);
    setLeaveMembers(newLeaveMembers);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, [startDate, endDate, allMembers, isLoadingAllMember]);

  // 신규 / 재등록 회원 매출 계산 //
  useEffect(() => {
    if (salesData.length === 0 || allMembers.length === 0) return;

    const newMembersList = [];
    const reRegisteredMembersList = [];
    let calculatedNewPrice = 0;
    let calculatedReRegisteredPrice = 0;

    // 산정기간 내 approvalDate가 있는 usage가 첫 이용권이면 신규, 아니면 재등록 //
    for (let i = 0; i < allMembers.length; i++) {
      for (let j = 0; j < allMembers[i].usages.length; j++) {
        if (startDate.isSameOrBefore(allMembers[i].usages[j].approvalDate, 'day')
          && endDate.isSameOrAfter(allMembers[i].usages[j].approvalDate, 'day')) {
          if (j === 0) newMembersList.push(allMembers[i]);
          else reRegisteredMembersList.push(allMembers[i]);
          break;
        }
      }
    }

    // memberId 기반 O(1) lookup 위해 Set 변환 //
    const newMemberIds = new Set(newMembersList.map(m => m.memberId));
    const reRegisteredMemberIds = new Set(reRegisteredMembersList.map(m => m.memberId));

    for (let i = 0; i < salesData.length; i++) {
      const totalPrice = salesData[i].cardPrice + salesData[i].cashPrice;

      if (newMemberIds.has(salesData[i].memberId)) calculatedNewPrice += totalPrice;
      if (reRegisteredMemberIds.has(salesData[i].memberId)) calculatedReRegisteredPrice += totalPrice;
    }

    setNewPrice(calculatedNewPrice);
    setReRegisteredPrice(calculatedReRegisteredPrice);
  }, [salesData, allMembers]);

  // 계산식 호버 진입 //
  function onMouseOverFormula() {
    setIsViewFormulaModal(true);
  }

  // 계산식 호버 종료 //
  function onMouseLeaveFormula() {
    setIsViewFormulaModal(false);
  }

  // 회원 클릭시 상세 페이지 이동 //
  function onClickMember(memberId) {
    if (selectedPartnerGym === undefined || selectedPartnerGym.isMyStore) {
      window.open('/member/valid/detail/' + memberId, '_blank');
    }
    else {
      window.open('/member/partner_gym/detail/' + memberId, '_blank');
    }
  }

  // 사람 아이콘 비율 렌더링 (currentRate 기준 10개 중 채워질 개수 결정) //
  function renderPeopleIcons() {
    const totalIcons = 10;
    const filledCount = Math.round((currentRate / 100) * totalIcons);

    return (
      <PeopleIconsWrapper>
        <PeopleRow>
          {Array.from({ length: 5 }).map((_, i) => (
            <StyledPerson key={'StyledPersonTop-' + i} color={i < filledCount ? accentColor : theme.line} />
          ))}
        </PeopleRow>
        <PeopleRow>
          {Array.from({ length: 5 }).map((_, i) => {
            const idx = 5 + i;
            return (
              <StyledPerson key={'StyledPersonBottom-' + i} color={idx < filledCount ? accentColor : theme.line} />
            );
          })}
        </PeopleRow>
      </PeopleIconsWrapper>
    );
  }

  // 현재 탭에 따른 데이터 //
  const isNewTab = selectedTab === 'new';
  const isReRegisteredTab = selectedTab === 'reregistered';
  const isChurnedTab = selectedTab === 'churned';

  let currentMembers;
  let currentPrice;
  let accentColor;
  let salesLabel;
  let accentColorHover;
  let rateLabel;
  let stateLabel;
  let listTitle;
  let formulaNumeratorLabel;
  let memberType;

  if (isNewTab) {
    currentMembers = newMembers;
    currentPrice = newPrice;
    accentColor = theme.primary;
    accentColorHover = theme.primaryAlpha50;
    salesLabel = t('statistics.memberStatus.newSales');
    rateLabel = t('statistics.memberStatus.newRate');
    stateLabel = t('statistics.memberStatus.new');
    listTitle = t('statistics.memberStatus.newList');
    formulaNumeratorLabel = t('statistics.memberStatus.formulaNewNumerator');
    memberType = 'new';
  }
  else if (isReRegisteredTab) {
    currentMembers = reRegisteredMembers;
    currentPrice = reRegisteredPrice;
    accentColor = theme.subKey;
    accentColorHover = theme.subKeyAlpha50;
    salesLabel = t('statistics.memberStatus.reRegisteredSales');
    rateLabel = t('statistics.memberStatus.reRegisteredRate');
    stateLabel = t('statistics.memberStatus.reRegistered');
    listTitle = t('statistics.memberStatus.reRegisteredList');
    formulaNumeratorLabel = t('statistics.memberStatus.formulaReRegisteredNumerator');
    memberType = 'reregistered';
  }
  else {
    currentMembers = leaveMembers;
    currentPrice = 0;
    accentColor = theme.pointRed;
    accentColorHover = theme.pointRedAlpha50;
    salesLabel = '';
    rateLabel = t('statistics.memberStatus.churnRate');
    stateLabel = t('statistics.memberStatus.churned');
    listTitle = t('statistics.memberStatus.churnedList');
    formulaNumeratorLabel = t('statistics.memberStatus.formulaChurnedNumerator');
    memberType = 'churned';
  }

  let currentRate = 0;
  if (aliveMembers.length > 0) currentRate = Math.round((currentMembers.length / aliveMembers.length) * 100);

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          !isOpenPartnerGymSales ?
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
            :
            <MainContentWrapper>
              <HeaderWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>{calcDateText()}</Text>
                <RowWrapper $gap={5}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                    {t('statistics.memberStatus.memberCount')}
                  </Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={(newMembers.length - leaveMembers.length) >= 0 ? theme.primary : theme.subKey} cursor='default'>
                    {(newMembers.length - leaveMembers.length) > 0 ? '+' : ''}{newMembers.length - leaveMembers.length}
                  </Text>
                </RowWrapper>
              </HeaderWrapper>

              {/* 상단 정보 행 */}
              <InfoRow>
                <InfoLeft>
                  {
                    isChurnedTab ?
                      <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                        {t('statistics.memberStatus.churnedMembers')}
                      </Text>
                      :
                      <RowWrapper $gap={10}>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                          {salesLabel}
                        </Text>
                        <RowWrapper $gap={5}>
                          <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text} cursor='default'>
                            {currentPrice.toLocaleString('ko-KR')}
                          </Text>
                          <Text fontFamily='NotoSansKR-Regular' fontSize={20} color={theme.subText} cursor='default'>
                            {t('statistics.won')}
                          </Text>
                        </RowWrapper>
                      </RowWrapper>
                  }
                </InfoLeft>
                <InfoRight>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text} cursor='default'>
                    {rateLabel}
                  </Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={accentColor} cursor='default'>
                    {currentRate}%
                  </Text>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={accentColor} cursor='default'>
                    {'[ ' + t('statistics.memberMovement.total') + ' ' + currentMembers.length + t('statistics.personUnit') + ' ' + stateLabel + ' ]'}
                  </Text>
                </InfoRight>
              </InfoRow>

              {/* 메인 컨텐츠 영역 */}
              <ContentRow>
                {/* 왼쪽: 인포그래픽 + 계산식 + 토글 */}
                <LeftColumn>
                  <InfographicArea
                    onMouseOver={onMouseOverFormula}
                    onMouseLeave={onMouseLeaveFormula}>
                    {renderPeopleIcons()}

                    <CalculationFormulaModal
                      isView={isViewFormulaModal}
                      denominator={currentMembers.length.toLocaleString('ko-KR')}
                      denominatorText={formulaNumeratorLabel}
                      numerator={aliveMembers.length.toLocaleString('ko-KR')}
                      numeratorText={t('statistics.memberStatus.formulaDenominator')}
                      result={currentRate}
                      resultUnit='%'
                      innerBoxBackgroundColor={theme.buttonFill}
                      componentBoxBackgroundColor={theme.subBackground}
                      accentColor={accentColor}
                      top={50}
                      left={20} />
                  </InfographicArea>

                  <ToggleWrapper>
                    <ToggleButton
                      $isActive={isNewTab}
                      $activeColor={theme.primary || '#FF8333'}
                      onClick={() => setSelectedTab('new')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={isNewTab ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.memberStatus.tabNew')}
                      </Text>
                    </ToggleButton>
                    <ToggleButton
                      $isActive={isReRegisteredTab}
                      $activeColor='#6DD49E'
                      onClick={() => setSelectedTab('reregistered')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={isReRegisteredTab ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.memberStatus.tabReRegistered')}
                      </Text>
                    </ToggleButton>
                    <ToggleButton
                      $isActive={isChurnedTab}
                      $activeColor={theme.pointRed}
                      onClick={() => setSelectedTab('churned')}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={15} color={isChurnedTab ? theme.subBackground : theme.text} cursor='pointer'>
                        {t('statistics.memberStatus.tabChurned')}
                      </Text>
                    </ToggleButton>
                  </ToggleWrapper>
                </LeftColumn>

                {/* 오른쪽: 회원 목록 */}
                <RightColumn>
                  <ListTitleRow>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>
                      {listTitle}
                    </Text>
                  </ListTitleRow>

                  <MemberStatusListTable
                    members={currentMembers}
                    memberType={memberType}
                    accentColor={accentColor}
                    accentColorHover={accentColorHover}
                    onClickMember={onClickMember} />
                </RightColumn>

              </ContentRow>
            </MainContentWrapper>
      }
    </TopLevelWrapper>
  );
};

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  margin: 38px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;
  height: 412px;

  gap: 10px;

  border-radius: 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 40px 50px rgba(240, 232, 227, 0.5)'};
  background-color: ${props => props.theme.subBackground};
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 448px;

  z-index: 5;

  background-color: ${props => props.theme.subBackground};
  border-radius: 20px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 448px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const InfoLeft = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
`;

const InfoRight = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: ${props => props.$gap}px;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;

  gap: 30px;

  width: 100%;
  height: 338px;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  justify-content: space-between;
  align-items: center;

  padding: 0 20px;

  overflow: clip;
`;

const InfographicArea = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  gap: 10px;

  width: 400px;
  flex: 1;

  cursor: pointer;
`;

const PeopleRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-end;

  gap: 15px;
`;

const PeopleIconsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  gap: 10px;
`;

const StyledPerson = styled(PersonSilhouetteSvg)`
  width: 50px;
  height: 109px;

  color: ${props => props.color};
`;

const ToggleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 5px;

  gap: 5px;

  height: 41px;

  margin-bottom: 20px;

  border-radius: 8px;
  background-color: ${props => props.theme.buttonFill3 || '#282828'};

  box-sizing: border-box;
`;

const ToggleButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 110px;
  height: 29px;

  border-radius: 6px;
  background-color: ${props => props.$isActive ? props.$activeColor : 'transparent'};
  box-shadow: ${props => props.$isActive ? '0px 9px 7.1px 0px rgba(0, 0, 0, 0.06)' : 'none'};

  cursor: pointer;

  transition: all 0.3s ease-in-out;
`;

/* 오른쪽 회원 목록 영역 */
const RightColumn = styled.div`
  display: flex;
  flex-direction: column;

  gap: 10px;

  width: 480px;
`;

const ListTitleRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding-top: 20px;

  width: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  white-space: nowrap;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
